import { Formik } from 'formik';

import { withReactQuery } from '@/react-tools/withReactQuery';
import { withCurrentUser } from '@/react-tools/withCurrentUser';
import { useHelmRepoVersions } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmRepoVersions';
import { withUIRouter } from '@/react-tools/withUIRouter';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import {
  HelmRelease,
  UpdateHelmReleasePayload,
} from '@/react/kubernetes/helm/types';
import { useHelmRegistryRepoOptions } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmRegistries';

import { OnSubmit, openModal } from '@@/modals';

import { UpgradeHelmInnerForm } from './UpgradeHelmInnerForm';
import { UpgradeHelmFormValues } from './types';
import { useChartReferenceState } from './utils/useChartReferenceState';
import { validation } from './validation';

interface Props {
  onSubmit: OnSubmit<UpdateHelmReleasePayload>;
  release: HelmRelease;
}

export function UpgradeHelmModal({ release, onSubmit }: Props) {
  const environmentId = useEnvironmentId();
  const defaultVersionQuery = useHelmRepoVersions(
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      select: (versions) => versions?.[0],
    },
    release.chartReference,
    true,
    false
  );

  const registryOptionsQuery = useHelmRegistryRepoOptions(
    environmentId,
    release.namespace
  );
  const chartReferenceState = useChartReferenceState(
    release,
    registryOptionsQuery.registryOptions
  );

  const initialValues: UpgradeHelmFormValues = {
    version:
      release.chart.metadata?.version ??
      defaultVersionQuery.data?.version ??
      '',
    repo: chartReferenceState.selectedRegistry?.registryUrl,
    registryId: chartReferenceState.selectedRegistry?.registryId,
    chart: chartReferenceState.selectedChartName ?? '',
    values: release.values?.userSuppliedValues ?? '',
    atomic: true,
  };

  const isCheckingChartReference =
    chartReferenceState.source.isChecking ||
    chartReferenceState.chart.isChecking ||
    registryOptionsQuery.isLoading;

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnMount
      onSubmit={handleSubmit}
      validationSchema={validation}
    >
      <UpgradeHelmInnerForm
        release={release}
        onDismiss={() => onSubmit()}
        chartReferenceInfoMessage={chartReferenceState.infoAlert}
        isCheckingChartReference={isCheckingChartReference}
        initialRegistryValue={chartReferenceState.selectedRegistry}
        chartReferenceState={{
          requiresSourceConfirmation:
            chartReferenceState.requiresSourceConfirmation,
          requiresChartConfirmation:
            chartReferenceState.requiresChartConfirmation,
        }}
      />
    </Formik>
  );

  async function handleSubmit(values: UpgradeHelmFormValues) {
    onSubmit({
      name: release.name,
      namespace: release.namespace,
      values: values.values,
      chart: values.chart,
      repo: values.repo,
      registryId: values.registryId,
      version: values.version,
      atomic: values.atomic,
    });
  }
}

export async function openUpgradeHelmModal(release: HelmRelease) {
  return openModal(
    withUIRouter(withReactQuery(withCurrentUser(UpgradeHelmModal))),
    {
      release,
    }
  );
}
